
import React, { useRef, useState } from 'react';
import { Widget, WeatherData, SearchQuery, SearchType, Hotel } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { SearchWidget } from './SearchWidget'; // We might reuse search widget
import { useNavigate } from 'react-router-dom';

// --- Helper Components ---

const SectionContainer: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className = '' }) => (
    <div className={`py-12 md:py-20 ${className}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {children}
        </div>
    </div>
);

// --- Widgets ---

/**
 * 1. Hero Widget
 * Fully dynamic hero with video support
 */
export const HeroWidget: React.FC<{ settings: any, content: any, title: string, subtitle: string }> = ({ settings, content, title, subtitle }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { language } = useLocalization();

    // Use content from widget if available, or fallbacks passed from parent
    const heroImage = content?.image || settings?.fallbackImage || "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1";
    const videoUrl = content?.video;

    return (
        <div className="relative h-[85vh] min-h-[600px] w-full overflow-hidden group">
            {videoUrl ? (
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[20s]"
                    src={videoUrl}
                    poster={heroImage}
                    autoPlay
                    muted
                    loop
                    playsInline
                />
            ) : (
                <img
                    src={heroImage}
                    className="absolute inset-0 w-full h-full object-cover animate-slow-zoom"
                    alt={title}
                />
            )}

            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70"></div>

            <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center pb-20">
                <span className="inline-block py-1.5 px-5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-bold uppercase tracking-[0.3em] mb-8 animate-fade-in-up">
                    {language === 'sv' ? 'Destination' : language === 'da' ? 'Destinationsguide' : 'Destination Guide'}
                </span>
                <h1 className="text-5xl md:text-8xl font-black text-white mb-6 drop-shadow-2xl tracking-tight leading-tight">
                    {title}
                </h1>
                <div className="text-xl md:text-3xl text-white/90 max-w-4xl font-light leading-relaxed drop-shadow-lg border-t border-white/20 pt-8 animate-fade-in-up-delay" dangerouslySetInnerHTML={{ __html: subtitle || '' }} />
            </div>
        </div>
    );
};

/**
 * 2. Weather Widget
 * Monthly horizontal scroll
 */
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_NAMES: Record<string, string[]> = {
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    sv: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
    da: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec']
};

export const WeatherWidget: React.FC<{ weatherData?: { [key: string]: WeatherData } }> = ({ weatherData }) => {
    const { language } = useLocalization();

    if (!weatherData) return null;

    const currentMonthIndex = new Date().getMonth();

    return (
        <SectionContainer className="bg-slate-50 border-y border-gray-100">
            <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
                <div className="md:w-1/3">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">{language === 'sv' ? 'Väder & Klimat' : language === 'da' ? 'Vejr & Klima' : 'Weather & Climate'}</h2>
                    <p className="text-gray-500">{language === 'sv' ? 'Genomsnittstemperaturer (°C)' : language === 'da' ? 'Gennemsnitstemperaturer (°C)' : 'Average temperatures (°C)'}</p>
                </div>
            </div>

            <div className="flex overflow-x-auto pb-6 gap-4 no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth">
                {MONTHS.map((monthKey, idx) => {
                    const data = weatherData[monthKey];
                    if (!data) return null;
                    const isCurrent = idx === currentMonthIndex;

                    return (
                        <div key={monthKey} className={`flex-shrink-0 w-24 md:w-32 flex flex-col items-center p-4 rounded-2xl border transition-all duration-300 ${isCurrent ? 'bg-primary text-white border-primary shadow-lg scale-110' : 'bg-white text-gray-800 border-gray-200 hover:border-primary/50'
                            }`}>
                            <span className={`text-sm font-bold uppercase mb-3 ${isCurrent ? 'text-white/80' : 'text-gray-400'}`}>
                                {(MONTH_NAMES[language] || MONTH_NAMES.en)[idx]}
                            </span>
                            <div className="flex items-center justify-center w-12 h-12 mb-3">
                                {/* Simple icon based on temp roughly */}
                                {data.temp > 25 ? (
                                    <span className="material-icons-round text-3xl text-amber-500">wb_sunny</span>
                                ) : data.temp > 15 ? (
                                    <span className="material-icons-round text-3xl text-amber-300">wb_twilight</span>
                                ) : (
                                    <span className="material-icons-round text-3xl text-blue-300">ac_unit</span>
                                )}
                            </div>
                            <span className="text-2xl font-black">{data.temp}°</span>
                            {data.water && (
                                <div className={`flex items-center gap-1 mt-2 text-xs ${isCurrent ? 'text-white/90' : 'text-blue-500'}`}>
                                    <span className="material-icons-round text-[10px]">water_drop</span>
                                    {data.water}°
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </SectionContainer>
    );
};

/**
 * 3. Rich Text Widget
 * Left/Right image layout for history/nature
 */
export const RichTextWidget: React.FC<{ content: any, settings: any }> = ({ content, settings }) => {
    const { language } = useLocalization();
    const title = language === 'sv' ? content.titleSv : content.title;
    const text = language === 'sv' ? content.textSv : content.text;
    const image = content.image;
    const reverse = settings.alignment === 'right';

    if (!text) return null;

    return (
        <section className={`py-16 md:py-24 ${settings.background === 'gray' ? 'bg-gray-50' : 'bg-white'}`}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className={`flex flex-col md:flex-row items-center gap-16 ${reverse ? 'md:flex-row-reverse' : ''}`}>
                    <div className="w-full md:w-1/2">
                        {title && <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-8 leading-tight">{title}</h2>}
                        <div className="prose prose-lg text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: text }} />
                    </div>
                    {image && (
                        <div className="w-full md:w-1/2">
                            <div className={`relative rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/3] group transform transition-all duration-700 hover:scale-[1.02] ${reverse ? '-rotate-1 hover:rotate-0' : 'rotate-1 hover:rotate-0'
                                }`}>
                                <img src={image} alt={title || "Destination"} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 border-[6px] border-white/10 pointer-events-none"></div>
                            </div>
                            {/* Decorative element */}
                            <div className={`absolute -z-10 w-full h-full bg-primary/20 rounded-full blur-[100px] top-1/2 ${reverse ? 'right-0' : 'left-0'}`}></div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

/**
 * 4. Hotel Grid Widget (Reusing logic but simplified props)
 */
interface HotelGridProps {
    hotels: any[];
    title: string;
    loading: boolean;
    onBook: (hotel: any) => void;
}

export const HotelGridWidget: React.FC<HotelGridProps> = ({ hotels, title, loading, onBook }) => {
    const { language } = useLocalization();
    const [showTotalPrice, setShowTotalPrice] = useState(false);

    // Helper to get display price
    const getDisplayPrice = (hotel: any) => {
        if (showTotalPrice) {
            return hotel.totalPrice || hotel.price || 0;
        }
        return hotel.pricePerNight || Math.round((hotel.price || 0) / 7);
    };

    // Helper to get price label
    const getPriceLabel = () => {
        if (showTotalPrice) {
            return language === 'sv' ? 'Totalt' : language === 'da' ? 'Total' : 'Total';
        }
        return language === 'sv' ? '/natt' : language === 'da' ? '/nat' : '/night';
    };

    return (
        <section className="bg-slate-900 py-24 text-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row justify-between items-end mb-16">
                    <div>
                        <span className="text-amber-400 font-bold uppercase tracking-widest text-xs mb-3 block">
                            {language === 'sv' ? 'Exklusivt Utvalda' : language === 'da' ? 'Eksklusivt Udvalgte' : 'Exclusively Curated'}
                        </span>
                        <h2 className="text-3xl md:text-5xl font-black">{title}</h2>
                    </div>
                    <div className="flex items-center gap-4 mt-6 md:mt-0">
                        {/* Price Toggle Button */}
                        <button
                            onClick={() => setShowTotalPrice(!showTotalPrice)}
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-all text-sm font-medium border border-white/20"
                        >
                            <span className="text-xs text-white/60">
                                {language === 'sv' ? 'Visa:' : language === 'da' ? 'Vis:' : 'Show:'}
                            </span>
                            <span className="font-bold">
                                {showTotalPrice
                                    ? (language === 'sv' ? 'Totalpris' : language === 'da' ? 'Totalpris' : 'Total Price')
                                    : (language === 'sv' ? 'Pris/Natt' : language === 'da' ? 'Pris/Nat' : 'Per Night')
                                }
                            </span>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                        </button>
                        <button className="hidden md:block px-8 py-3 rounded-full border border-white/20 hover:bg-white hover:text-black transition-all font-medium">
                            {language === 'sv' ? 'Se alla hotell' : 'View all hotels'}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[1, 2, 3].map(i => <div key={i} className="h-96 bg-white/5 rounded-2xl animate-pulse"></div>)}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {hotels.map((hotel) => (
                            <div
                                key={hotel.hotelId}
                                className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden cursor-pointer hover:bg-white/10 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/20"
                                onClick={() => onBook(hotel)}
                            >
                                <div className="h-72 overflow-hidden relative">
                                    <img src={hotel.imageUrl} alt={hotel.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                    {(hotel.price || hotel.pricePerNight) && (
                                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md text-black px-4 py-2 rounded-lg shadow-lg">
                                            <span className="font-bold text-lg">
                                                {Math.round(getDisplayPrice(hotel)).toLocaleString('sv-SE')}
                                            </span>
                                            <span className="text-gray-600 text-sm ml-1">
                                                {language === 'sv' ? 'SEK' : language === 'da' ? 'DKK' : 'EUR'}
                                            </span>

                                            <span className="text-gray-500 text-xs ml-1">
                                                {getPriceLabel()}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="p-8">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="text-xl font-bold mb-1 group-hover:text-primary transition-colors">{hotel.name}</h3>
                                            <div className="flex items-center text-gray-400 text-sm">
                                                <span className="material-icons-round text-sm mr-1">location_on</span>
                                                {hotel.city}
                                            </div>
                                        </div>
                                        <div className="flex text-amber-400">
                                            {[...Array(5)].map((_, i) => (
                                                <span key={i} className="material-icons-round text-sm">star</span>
                                            ))}
                                        </div>
                                    </div>

                                    <button className="w-full mt-4 py-3 bg-white text-black font-bold rounded-xl hover:bg-primary hover:text-white transition-colors">
                                        {language === 'sv' ? 'Boka Nu' : language === 'da' ? 'Book Nu' : 'Book Now'}
                                    </button>

                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

// --- Main Widget Renderer ---

export const WidgetRenderer: React.FC<{
    widget: Widget,
    context: any
}> = ({ widget, context }) => {

    switch (widget.type) {
        case 'hero':
            return <HeroWidget settings={widget.settings} content={widget.content} title={context.title} subtitle={context.subtitle} />;

        case 'weather':
            return <WeatherWidget weatherData={context.weather} />;

        case 'rich_text':
            return <RichTextWidget content={widget.content} settings={widget.settings} />;

        case 'featured_hotels':
            // Logic handled in parent mostly, but we trigger the display
            return (
                <HotelGridWidget
                    hotels={context.featuredHotels}
                    title={context.featuredTitle}
                    loading={context.loadingHotels}
                    onBook={context.onBookHotel}
                />
            );

        default:
            return null;
    }
};
